import { Trash2 } from "lucide-react";
import { BillChargeKind, ChargeSplitMode } from "@boo/shared";
import type { EditorCharge } from "../types/bills.types";
import { INPUT_CLASS } from "../types/bills.types";
import NumberField from "./NumberField";

interface ChargesSectionProps {
  charges: EditorCharge[];
  readOnly: boolean;
  onAdd: (kind: BillChargeKind) => void;
  onChange: (key: string, patch: Partial<EditorCharge>) => void;
  onRemove: (key: string) => void;
}

const ADD_BUTTONS: { kind: BillChargeKind; label: string }[] = [
  { kind: BillChargeKind.Service, label: "+ Service" },
  { kind: BillChargeKind.Vat, label: "+ VAT" },
  { kind: BillChargeKind.Discount, label: "+ Discount" },
  { kind: BillChargeKind.Other, label: "+ Other" },
];

export default function ChargesSection({
  charges,
  readOnly,
  onAdd,
  onChange,
  onRemove,
}: ChargesSectionProps) {
  return (
    <section className="space-y-2.5">
      <h2 className="text-sm font-semibold text-foreground">
        Service, VAT &amp; discounts
      </h2>
      <ul className="space-y-2">
        {charges.map((c) => {
          const isDiscount = c.kind === BillChargeKind.Discount;
          const proportional = c.splitMode === ChargeSplitMode.Proportional;
          return (
            <li
              key={c.key}
              className="rounded-xl border border-border bg-card p-3"
            >
              <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
                <input
                  aria-label="Charge label"
                  value={c.label}
                  disabled={readOnly}
                  onChange={(e) => onChange(c.key, { label: e.target.value })}
                  className={`${INPUT_CLASS} min-w-0 basis-full sm:basis-0 sm:flex-1`}
                />
                <div className="flex flex-1 items-center justify-end gap-2 sm:flex-none">
                {isDiscount && (
                  <span className="text-xs text-muted-foreground">−</span>
                )}
                {/* A discount is typed as a positive number and stored negative. */}
                <NumberField
                  ariaLabel="Charge amount"
                  value={Math.abs(c.amount)}
                  disabled={readOnly}
                  onChange={(n) =>
                    onChange(c.key, { amount: isDiscount ? -n : n })
                  }
                  className="w-24 shrink-0"
                />
                {!readOnly && (
                  <button
                    type="button"
                    onClick={() => onRemove(c.key)}
                    aria-label="Delete charge"
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:text-destructive sm:h-8 sm:w-8"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
                </div>
              </div>
              <div className="mt-2 flex rounded-lg border border-border overflow-hidden text-xs">
                {[
                  { mode: ChargeSplitMode.Proportional, label: "By items" },
                  { mode: ChargeSplitMode.Equal, label: "Equal" },
                ].map(({ mode, label }) => (
                  <button
                    key={mode}
                    type="button"
                    disabled={readOnly}
                    aria-pressed={c.splitMode === mode}
                    onClick={() => onChange(c.key, { splitMode: mode })}
                    className={`flex-1 py-3 font-medium sm:py-1.5 ${
                      c.splitMode === mode
                        ? "bg-primary text-primary-foreground"
                        : "text-muted-foreground"
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
              <p className="mt-1 text-[11px] text-muted-foreground">
                {c.percent != null &&
                  `${c.percent}% of ${
                    c.kind === BillChargeKind.Vat ? "items + service" : "items"
                  }. `}
                {proportional
                  ? "Each person pays in proportion to what they ordered."
                  : "Split evenly between everyone."}
              </p>
            </li>
          );
        })}
      </ul>
      {!readOnly && (
        <div className="flex flex-wrap gap-2">
          {ADD_BUTTONS.map(({ kind, label }) => (
            <button
              key={kind}
              type="button"
              onClick={() => onAdd(kind)}
              className="min-h-11 rounded-full border border-dashed border-border px-4 py-2 text-xs text-muted-foreground hover:text-foreground active:scale-95 sm:min-h-0 sm:px-3 sm:py-1.5"
            >
              {label}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}
